import Box, { type BoxProps } from '@mui/material/Box';

import {
  SHEEN_ANIMATION,
  SHEEN_DELAY_MS,
  SHEEN_DURATION_MS,
  SHEEN_EASING,
  SHEEN_GRADIENT,
  sheenSweepKeyframes,
} from './sheenMotion';

type Props = BoxProps & {
  /**
   * Run the pass. False — the default — is the resting state and renders no
   * animation at all, which is what the prerendered HTML contains and what a
   * reduced-motion visitor keeps.
   *
   * Driven from `useSheenOnEnter`, which already folds in the reduced-motion
   * check, so a caller never tests the media query itself.
   */
  play?: boolean;
  /**
   * How long to wait before the pass starts, in milliseconds.
   *
   * Exists for a caller whose content has its own entrance: light crossing a
   * line that is still arriving reads as a rendering fault, so the ribbon
   * holds the sheen until its parts have settled. Headings that simply fade
   * in take the default.
   */
  delayMs?: number;
};

/**
 * Sends a single band of brand light across its children as they arrive, then
 * gets out of the way (§52). See `sheenMotion.ts` for why the light is behind
 * the text and not through it.
 *
 * Content-agnostic: it wraps whatever it is given and reads nothing about it,
 * so the same atom serves a band headline, a stat or a price. It adds no
 * typography, no colour and no spacing of its own — the children keep theirs.
 *
 * Decorative throughout. The layer is a pseudo-element with no pointer events,
 * invisible to assistive technology, and the text underneath is a normal
 * heading whether the pass runs, is skipped, or never gets the chance.
 */
export function Sheen({ play = false, delayMs = SHEEN_DELAY_MS, sx, ...rest }: Props) {
  return (
    <Box
      sx={[
        {
          position: 'relative',
          // The layer travels past both edges; this is what turns that travel
          // into a sweep across the heading instead of light sliding in from
          // off-screen beside it.
          overflow: 'hidden',
          // Keeps the children's stacking context local, so a `zIndex: 1` here
          // cannot reorder anything in the band around it.
          isolation: 'isolate',
          [`@keyframes ${SHEEN_ANIMATION}`]: sheenSweepKeyframes,
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            zIndex: 0,
            background: SHEEN_GRADIENT,
            opacity: 0,
            pointerEvents: 'none',
          },
          '& > *': { position: 'relative', zIndex: 1 },
        },
        play && {
          '&::before': {
            animation: `${SHEEN_ANIMATION} ${SHEEN_DURATION_MS}ms ${SHEEN_EASING} ${delayMs}ms 1 both`,
          },
        },
        // `useSheenOnEnter` already withholds `play` here, so this is the
        // belt-and-braces rule: a caller passing `play` straight through still
        // cannot animate a heading for a visitor who asked for less motion.
        {
          '@media (prefers-reduced-motion: reduce)': {
            '&::before': { animation: 'none', opacity: 0 },
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...rest}
    />
  );
}
